/**
 * The intent contract — what one answerable question type declares.
 *
 * An intent is a pair: a **scorer** (how strongly does this question ask for
 * me?) and an **answerer** (build the reply from the tools and the seeds).
 * `../router.ts` scores every intent in `./index.ts`, picks the best above a
 * threshold with a fixed-priority tiebreak, and hands the winner an
 * {@link AnswerCtx}.
 *
 * To add an intent: write an {@link IntentDef} in a family file, add it to
 * the `INTENTS` array in `./index.ts`, and add its cases to
 * `../intent-cases.ts`. `npm run check:assistant` does the rest.
 *
 * Engine layer — types and one pure scoring helper.
 */

import type { OemPersonaId } from '../../../schema/persona.ts';
import type { ConversationMemory, OemAnswer } from '../answer-types.ts';
import type { Entities } from '../entities.ts';
import type { AnswerKit } from '../answer-kit.ts';
import type { PageContext } from '../page-context.ts';

/** The families the fallback groups its help by. */
export type IntentFamily =
  | 'fleet'
  | 'machine'
  | 'cost'
  | 'parts'
  | 'work'
  | 'service'
  | 'knowledge'
  | 'platform'
  | 'reports'
  | 'energy'
  | 'operations';

/** What a scorer sees. */
export interface ScoreCtx {
  /** The raw question, trimmed. */
  readonly q: string;
  /** Lowercased. */
  readonly L: string;
  readonly page: PageContext;
  readonly memory?: ConversationMemory;
}

/** What an answerer sees. */
export interface AnswerCtx extends ScoreCtx {
  readonly E: Entities;
  readonly personaId: OemPersonaId;
  readonly pathname: string;
  /** Tool runner, trace, `done()` builder, focus and memory. */
  readonly kit: AnswerKit;
}

/** Entities an intent cannot answer without. The router fills them from page / memory. */
export type IntentNeed = 'machine' | 'mine' | 'module' | 'report';

/** One answerable question type. */
export interface IntentDef {
  /** Stable id, used by cases, memory and the fallback. */
  readonly id: string;
  readonly family: IntentFamily;
  /**
   * Tiebreak — higher wins an equal score. The ported demo branches keep
   * their original order this way (earlier branch, higher priority).
   */
  readonly priority: number;
  /** Two or three prompts it answers; the fallback offers the nearest. */
  readonly examples: readonly string[];
  /** Entities it cannot answer without. Missing → filled from page/memory, else score 0. */
  readonly needs?: readonly IntentNeed[];
  /** 0 = not this intent. Higher = stronger. */
  score(L: string, E: Entities, ctx: ScoreCtx): number;
  answer(ctx: AnswerCtx): Promise<OemAnswer>;
}

// ---------------------------------------------------------------------------
// Scoring helper
// ---------------------------------------------------------------------------

/**
 * A declarative scorer.
 *
 * - `any` — at least one must match, else 0. The first match scores `base`
 *   (default 3); each further match adds 0.5.
 * - `all` — every one must match, else 0.
 * - `not` — any match → 0 (a hard exclusion).
 * - `boost` / `penalty` — `[pattern, weight]` added / subtracted.
 * - `when` — an entity gate: false → 0.
 * - `bonus` — extra weight from entities.
 */
export interface CueSpec {
  readonly any?: readonly RegExp[];
  readonly all?: readonly RegExp[];
  readonly not?: readonly RegExp[];
  readonly boost?: readonly (readonly [RegExp, number])[];
  readonly penalty?: readonly (readonly [RegExp, number])[];
  readonly when?: (E: Entities, ctx: ScoreCtx) => boolean;
  readonly bonus?: (E: Entities, ctx: ScoreCtx) => number;
  readonly base?: number;
}

export function cues(spec: CueSpec): IntentDef['score'] {
  return (L, E, ctx) => {
    if (spec.not?.some((re) => re.test(L))) return 0;
    if (spec.all && !spec.all.every((re) => re.test(L))) return 0;
    let s = 0;
    if (spec.any) {
      const hits = spec.any.filter((re) => re.test(L)).length;
      if (!hits) return 0;
      s = (spec.base ?? 3) + (hits - 1) * 0.5;
    } else {
      s = spec.base ?? 3;
    }
    if (spec.when && !spec.when(E, ctx)) return 0;
    for (const [re, w] of spec.boost ?? []) if (re.test(L)) s += w;
    for (const [re, w] of spec.penalty ?? []) if (re.test(L)) s -= w;
    if (spec.bonus) s += spec.bonus(E, ctx);
    return Math.max(0, s);
  };
}
